import firebase from 'firebase/app'
import 'firebase/firestore'
import firebaseui from 'firebaseui'

import router from '@/router'
import { store } from '@/store'

import { languages, ILesson, IUser, ITranslation, ITestResult } from '@/models'

const config = { // externalize this and make it a secret
  apiKey: "AIzaSyC_H3Rgo38Pkq9xWULpJYkuy1-JaM-d6p8",
  authDomain: "vocab-650e5.firebaseapp.com",
  databaseURL: "https://vocab-650e5.firebaseio.com",
  projectId: "vocab-650e5",
  storageBucket: "vocab-650e5.appspot.com",
  messagingSenderId: "829053064589"
}

namespace Firestore {
  let db: firebase.firestore.Firestore
  let ui: firebaseui.auth.AuthUI

  const uiConfig = {
    signInSuccessUrl: '/',
    signInOptions: [
      firebase.auth.GoogleAuthProvider.PROVIDER_ID
    ],
    signInFlow: 'popup'
  }

  export async function init() {
    const app = firebase.initializeApp(config)

    db = firebase.firestore()
    db.settings({ timestampsInSnapshots: true }) // disable deprecated features (required)
    await db.enablePersistence()

    return new Promise<IUser | null>(resolve => {
      firebase.auth().onAuthStateChanged(async (user: firebase.User | null) => {
        console.debug('[Firestore] onAuthStateChanged', user)
        if (! user) {
          console.debug('[Firestore] redirecting to sign-in page')
          store.setUser(null)
          router.push('/auth')
          resolve(null)
        }
        else {
          console.debug('[Firestore] sign-in successful')
          if (! user.email) throw new Error('Cannot authenticate a user without an email !')
          // save user on successfull login
          let iUser = await Firestore.Users.getByEmail(user.email)
          if (! iUser) {
            iUser = {
              uid: user.uid,
              email: user.email || '???',
              displayName: user.displayName || '???',
              photoURL: user.photoURL,
              isAdmin: false
            }
            store.saveUser(iUser)
          }
          store.setUser(iUser)
          console.debug('[Firestore] redirecting to sign-in success page')
          router.push((uiConfig!.signInSuccessUrl) as string)
          resolve(iUser)
        }
        // let requireAuth = this.context.$route.matched.some(record => record.meta.requireAuth)
        // let guestOnly = this.context.$route.matched.some(record => record.meta.guestOnly)
  
        // if(requireAuth && !user) this.context.$router.push('auth')
        // else if (guestOnly && user) this.context.$router.push('dashboard')
      })  
    })
  }

  export function logout() {
    return firebase.auth().signOut()
  }

  export function mountUI(element: string | Element) {
    if (ui)
      ui.reset()
    else
      ui = new firebaseui.auth.AuthUI(firebase.auth())
    ui.start(element, uiConfig)
    ui.disableAutoSignIn()
  }

  export namespace Users {
    const USERS_COL = 'users'
    /**
     * add user if it does not already exists
     * @returns true if added, false if it already existed
     */
    export async function add(user: IUser) {
      const docRef = await db.collection(USERS_COL).doc(user.email)
      const ds = await docRef.get()
      const data = ds.data()
      if (data && data.uid) return false
      else {
        return docRef.set(user).then(_ => true)
      }
    }

    export async function getByEmail(email: string): Promise<IUser | undefined> {
      const docRef = await db.collection(USERS_COL).doc(email)
      return docRef.get().then(ds => {
        if (ds.exists)
          return ds.data() as IUser
        else
          return undefined
      })
    }

    export async function list() {
      return db.collection(USERS_COL).get().then(qs => qs.docs.map(_ => _.data() as IUser))
    }
  }

  export namespace Lessons {
    const LES_COL = 'lessons'
    const WORDS_COL = 'words'

    export async function getLessons(): Promise<ILesson[]> {
      return db.collection(LES_COL).get().then(qs => qs.docs.map(qds => {
        const lesson = qds.data()
        lesson._id = qds.id
        lesson.words = []
        // get words
        qds.ref.collection(WORDS_COL).orderBy('order').get().then(qs2 => qs2.forEach(qds2 => {
          const tsl = qds2.data()
          tsl._id = qds2.id
          lesson.words.push(tsl as ITranslation)
        }))
        return lesson as ILesson
      }))
    }

    async function getWords(lessonDocRef: firebase.firestore.DocumentReference) {
      lessonDocRef
    }

    export async function create(lesson: ILesson): Promise<ILesson> {
      return save(lesson)
    }

    // save the whole lesson
    export async function save(lesson: ILesson) {
      const batch = db.batch()
      const lesRef = db.collection(LES_COL).doc(lesson._id)
      batch.set(lesRef, {
        created: lesson.created,
        creator: lesson.creator,
        name: lesson.name,
        srcLang: lesson.srcLang,
        tgtLang: lesson.tgtLang
      })
      const wordsCol = lesRef.collection(WORDS_COL)
      lesson.words.forEach(tsl => {
        let tslDoc: firebase.firestore.DocumentReference
        if (tsl._id) 
          tslDoc = wordsCol.doc(tsl._id) // update doc
        else {
          tslDoc = wordsCol.doc() // create doc
          tsl._id = tslDoc.id
        }
        batch.set(tslDoc, tsl)
      })
      return batch.commit().then(_ => lesson)
    }

    export async function saveWord(lessonId: string, word: ITranslation) {
      const wordsCol = db.collection(LES_COL).doc(lessonId).collection(WORDS_COL)
      let tslDoc: firebase.firestore.DocumentReference
      if (word._id) 
        tslDoc = wordsCol.doc(word._id) // update doc
      else {
        tslDoc = wordsCol.doc() // create doc
        word._id = tslDoc.id
      }
      return tslDoc.set(word).then(_ => word)
    }

    export async function saveWords(lessonId: string, words: ITranslation[]) {
      const wordsCol = db.collection(LES_COL).doc(lessonId).collection(WORDS_COL)
      return Promise.all(words.map(word => {
        let tslDoc: firebase.firestore.DocumentReference
        if (word._id) 
          tslDoc = wordsCol.doc(word._id) // update doc
        else {
          tslDoc = wordsCol.doc() // create doc
          word._id = tslDoc.id
        }
        return tslDoc.set(word).then(_ => word)
      }))
    }

    export async function deleteWord(lessonId: string, word: ITranslation) {
      if (! word._id) throw new Error('Cannot delete word, it has no id: ' + word)
      const wordsCol = db.collection(LES_COL).doc(lessonId).collection(WORDS_COL)
      return wordsCol.doc(word._id).delete()
    }

    export async function deleteLesson(lessonId: string) {
      const batch = db.batch()
      const lesRef = db.collection(LES_COL).doc(lessonId)
      const wordDocs = await lesRef.collection(WORDS_COL).get().then(_ => _.docs)
      wordDocs.forEach(_ => batch.delete(_.ref))
      batch.delete(lesRef)
      return batch.commit()
    }
  }

  export namespace TestResults {
    const TR_COL = 'test-results'

    export async function get(testResultId: string) {
      return db.collection(TR_COL).doc(testResultId).get().then(_ => _.data() as ITestResult | undefined)
    }

    export async function save(testResult: ITestResult) {
      let doc: firebase.firestore.DocumentReference
      if (testResult._id)
        doc = db.collection(TR_COL).doc(testResult._id)
      else {
        doc = db.collection(TR_COL).doc()
        testResult._id = doc.id
      }
      return doc.set(testResult)
    }

    export async function list(descending: boolean = false) {
      return db.collection(TR_COL).orderBy('_id', descending ? 'desc' : 'asc')
        .get().then(qs => qs.docs.map(qds => qds.data() as ITestResult))
    }

    export async function listByUser(uid: string) {
      return db.collection(TR_COL).where('user.uid', '==', uid)
        .get().then(qs => qs.docs.map(qds => qds.data() as ITestResult))
    }

    export async function deleteResult(testResultId: string) {
      return db.collection(TR_COL).doc(testResultId).delete()
    }
  }
}

export default Firestore